import type { TFunction } from 'i18next';
import type {
  BaseEvaluationReport,
  EvaluationReportForComputerScience,
  EvaluationReportForMathAndMathComputerScience,
} from '../../../Types/interfaces';
import { StudyLineType } from '../../../Types/interfaces';

export const calcKnowledgePoints = (
  evaluationReport:
    | EvaluationReportForComputerScience
    | EvaluationReportForMathAndMathComputerScience
): number => {
  if (!evaluationReport) return -1;

  let specificPoints = 0;

  // calculating base points for all majors
  const basePoints =
    (evaluationReport.studentsKnowledgeWhenStarting ?? 0) +
    (evaluationReport.qualityOfWork ?? 0) +
    (evaluationReport.qualityOfDocumentation ?? 0);

  // calculating ComputerScince major
  if (evaluationReport.studyLineType === StudyLineType.ComputerScience) {
    specificPoints =
      (evaluationReport.pointsForAlgorithmsAndDataStructures ?? 0) +
      (evaluationReport.pointsForProgrammingLanguagesAndEnvironments ?? 0) +
      (evaluationReport.pointsForOperatingSystemsAndComputerNetworks ?? 0) +
      (evaluationReport.pointsForSoftwareArchitectures ?? 0) +
      (evaluationReport.pointsForSoftwareMethodologies ?? 0) +
      (evaluationReport.pointsForDatabases ?? 0) +
      (evaluationReport.pointsForTesting ?? 0) +
      (evaluationReport.pointsForSecurity ?? 0) +
      (evaluationReport.pointsForOtherDevelopmentSkills ?? 0);
  }

  // calculating Math and Math-ComputerScience major
  if (
    evaluationReport.studyLineType === StudyLineType.MathComputerScience ||
    evaluationReport.studyLineType === StudyLineType.Math
  ) {
    specificPoints =
      (evaluationReport.pointsForFundamentalMathematicalStructures ?? 0) +
      (evaluationReport.pointsForAlgebraAnalysisGeometryAndDifferentialEquations ??
        0) +
      (evaluationReport.pointsForMathematicalModeling ?? 0) +
      (evaluationReport.pointsForProbabilityAndStatistics ?? 0) +
      (evaluationReport.pointsForTeachingMethodologyAndDidactics ?? 0) +
      (evaluationReport.pointsForOtherMathematicalSkills ?? 0);
  }

  return basePoints + specificPoints;
};

export const calcSoftSkillPoints = (
  evaluationReport: BaseEvaluationReport
): number => {
  if (!evaluationReport) return -1;

  return (
    (evaluationReport.taskAdherence ?? 0) +
    (evaluationReport.terminologyUsage ?? 0) +
    (evaluationReport.pointsForTeamWork ?? 0) +
    (evaluationReport.pointsForResponsibility ?? 0) +
    (evaluationReport.pointsForInitiative ?? 0) +
    (evaluationReport.pointsForEffortCapacity ?? 0) +
    (evaluationReport.pointsForAdaptationCapacity ?? 0) +
    (evaluationReport.pointsForOtherSoftSkills ?? 0)
  );
};

export const getKnowledgeGrade = (value: number, t: TFunction): string => {
  if (value === 0.1) return t('evalReportDisplay.poor');
  if (value === 0.2) return t('evalReportDisplay.satisfactory');
  if (value === 0.3) return t('evalReportDisplay.good');
  if (value === 0.4) return t('evalReportDisplay.vgood');
  if (value === 0.5) return t('evalReportDisplay.excellent');
  return t('evalReportDisplay.unknown');
};

export const getDocumentationGrade = (value: number, t: TFunction): string => {
  if (value === 0) return t('evalReportDisplay.notcase');
  if (value === 0.1) return t('evalReportDisplay.superficial');
  if (value === 0.25) return t('evalReportDisplay.incomplete');
  if (value === 0.5) return t('evalReportDisplay.complete');
  return t('evalReportDisplay.unknown');
};

export const getGainedKnowledgeGrade = (
  value: number,
  t: TFunction
): string => {
  if (value === 0) return t('evalReportDisplay.noteval');
  if (value === 0.3) return t('evalReportDisplay.vPoor');
  if (value === 0.5) return t('evalReportDisplay.satisfactory');
  if (value === 0.7) return t('evalReportDisplay.good');
  if (value === 0.9) return t('evalReportDisplay.vgood');
  return t('evalReportDisplay.unknown');
};

export const getSoftSkillGrade = (value: number, t: TFunction): string => {
  if (value === 0) return t('evalReportDisplay.notall');
  if (value === 0.2) return t('evalReportDisplay.smallExtent');
  if (value === 0.4) return t('evalReportDisplay.largextent');
  if (value === 0.5) return t('evalReportDisplay.completely');
  return t('evalReportDisplay.unknown');
};

export const getQualitiesGrade = (value: number, t: TFunction): string => {
  if (value === 0) return t('evalReportDisplay.noteval');
  if (value === 0.5) return t('evalReportDisplay.demonstrated');
  return t('evalReportDisplay.unknown');
};

export function formatNumberWithMaxDecimals(number: number): string {
  const roundedNumber = Math.round(number * 100) / 100;
  const decimalPart = roundedNumber - Math.floor(roundedNumber);

  if (decimalPart === 0) {
    return roundedNumber.toFixed(0);
  } else if (decimalPart * 10 === 0) {
    return roundedNumber.toFixed(1);
  } else {
    return roundedNumber.toFixed(2);
  }
}
